import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PostFormModalWrapper from './PostFormModalWrapper';
import PostForm from '../PostForm';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog/ConfirmDeleteDialog';
import { useUser } from '../../config/UserContext';
import { useSongs } from '../../config/SongContext';
import hasPostedToday from '../utils/hasPostedToday';
import styles from './PostFormModal.module.scss';

// DEV: set to true to freeze the posting overlay for styling
const DEV_FORCE_POSTING = false;
const DEV_POSTING_FIXTURE = {
  thumbnailURL: 'https://i.ytimg.com/vi/dQw4w9WgXcQ/maxresdefault.jpg',
  songName: 'Never Gonna Give You Up',
  songArtist: 'Rick Astley',
};

const POSTING_MESSAGES = [
  'Warming up the subs...',
  'Passing the aux...',
  'Adding to the playlist...',
  'Getting the crowd hyped...',
  'Tuning the frequencies...',
  'Loading up the vibes...',
  'Queuing the banger...',
  'Making it slap...',
  'Amplifying the signal...',
  'Cueing the drop...',
  'Feeling the bassline...',
  'Almost there...',
];

const PostFormModal = ({
  show,
  onClose,
  isEditing = false,
  postToEdit = null,
}) => {
  const { currentUser } = useUser();
  const { songs, deleteSong } = useSongs();
  const [isPosting, setIsPosting] = useState(false);
  const [postingSongData, setPostingSongData] = useState(null);
  const [messageIndex, setMessageIndex] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handlePostingChange = (posting, songData) => {
    setIsPosting(posting);
    if (posting && songData) setPostingSongData(songData);
    if (!posting) setPostingSongData(null);
  };

  useEffect(() => {
    if (!isPosting && !DEV_FORCE_POSTING) {
      setMessageIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setMessageIndex((i) => (i + 1) % POSTING_MESSAGES.length);
    }, 1800);
    return () => clearInterval(interval);
  }, [isPosting]);

  const todaysSong = currentUser
    ? hasPostedToday(songs, currentUser.uid)
    : null;

  const hasPostedResult = currentUser
    ? hasPostedToday(songs, currentUser.uid)
    : null;

  const thumbnailURL = hasPostedResult ? hasPostedResult.thumbnailURL : null;

  return (
    <PostFormModalWrapper show={show} onClose={onClose}>
      {todaysSong && !isEditing ? (
        <div className={styles.container}>
          <div className={styles.group}>
            <div className={styles.card}>
              <h3 className={styles.title}>Thanks for posting today!</h3>
              <div className={styles.songInfo}>
                <div className={styles.songInfoInner}>
                  <img src={thumbnailURL} alt="" />
                  <span className={styles.songName}>{todaysSong.songName}</span>
                  <span className={styles.artist}>{todaysSong.songArtist}</span>
                </div>
                <button
                  className={styles.deleteButton}
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete Song
                </button>
              </div>
            </div>
          </div>
          <ConfirmDeleteDialog
            show={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            confirm={() => {
              deleteSong(todaysSong.id);
              setShowDeleteConfirm(false);
            }}
            message="Are you sure you want to delete this song? This can't be undone."
          />
        </div>
      ) : (
        <div
          className={`${styles.loadingContainer} ${
            isPosting || DEV_FORCE_POSTING ? styles.loadingContainerPosting : ''
          }`}
        >
          {isPosting || DEV_FORCE_POSTING ? (
            (() => {
              const data = DEV_FORCE_POSTING
                ? DEV_POSTING_FIXTURE
                : postingSongData;
              return (
                <div className={styles.postingContainer}>
                  {data?.thumbnailURL && (
                    <div
                      className={styles.postingBg}
                      style={{ backgroundImage: `url(${data.thumbnailURL})` }}
                    />
                  )}
                  <div className={styles.postingBackdrop} />
                  <div className={styles.postingContent}>
                    <div className={styles.postingHeader}>
                      <h2 className={styles.postingHeaderTitle}>Nice one!</h2>
                      <p className={styles.postingHeaderSubtitle}>
                        Posting now...
                      </p>
                    </div>
                    <div className={styles.postingCard}>
                      <div className={styles.postingCardImage}>
                        {data?.thumbnailURL && (
                          <img
                            src={data.thumbnailURL}
                            alt={data.songName}
                            className={styles.postingCardThumbnail}
                          />
                        )}
                      </div>
                      <div className={styles.postingCardContent}>
                        {data?.songName && (
                          <p className={styles.postingSongName}>
                            {data.songName}
                          </p>
                        )}
                        {data?.songArtist && (
                          <p className={styles.postingSongArtist}>
                            {data.songArtist}
                          </p>
                        )}
                      </div>
                    </div>
                    <div
                      className={styles.postingLoader}
                      role="progressbar"
                      aria-label="Posting song"
                    >
                      <div className={styles.postingLoaderFill} />
                    </div>

                    <AnimatePresence mode="wait">
                      <motion.p
                        key={messageIndex}
                        className={styles.postingMessage}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.3 }}
                      >
                        {POSTING_MESSAGES[messageIndex]}
                      </motion.p>
                    </AnimatePresence>
                  </div>
                </div>
              );
            })()
          ) : (
            <PostForm
              onPostingChange={handlePostingChange}
              onSuccess={onClose}
              isEditing={isEditing}
              postToEdit={postToEdit}
            />
          )}
        </div>
      )}
    </PostFormModalWrapper>
  );
};

export default PostFormModal;
